import type {
  V1ValueType,
  Commonv1Value,
} from '@/generated/hooks/grimoire/models';
import type { InputComponentType, UnwrappedType } from '../types';

/**
 * Guards the wrapper-unwrapping loop below. `optional_type` nests arbitrarily
 * in the proto, but real specs use one level — this only exists so a malformed
 * (or cyclic-looking) type can't spin forever.
 */
const MAX_WRAPPER_DEPTH = 16;

/**
 * Unwraps optional type wrappers to get the base type.
 *
 * Loops to a fixpoint rather than checking once, so nesting depth doesn't
 * matter.
 *
 * `sensitive_type` is deliberately not handled: SPy has no sensitive/secret
 * annotation, so an automation's input specs can never carry one. Connection
 * and trigger config, which does deal in secrets, has its own handling (see
 * `WebhookTriggerDialog/ConfigInput`).
 */
export function unwrapType(type?: V1ValueType): UnwrappedType {
  let isOptional = false;
  let currentType: V1ValueType = type || {};

  for (let depth = 0; depth < MAX_WRAPPER_DEPTH; depth += 1) {
    if (currentType.optional_type?.inner) {
      isOptional = true;
      currentType = currentType.optional_type.inner;
      continue;
    }
    break;
  }

  return {
    baseType: currentType,
    isOptional,
  };
}

/**
 * Checks if a type is a list of files.
 *
 * The element type is unwrapped first so `list<optional<file>>` still resolves
 * to the multi-file uploader instead of falling back to the JSON textarea.
 */
export function isFileListType(type: V1ValueType): boolean {
  if (!type.list_type?.inner) return false;
  const { baseType } = unwrapType(type.list_type.inner);
  return baseType.scalar_type === 'VALUE_SCALAR_TYPE_FILE';
}

export function isBooleanType(type?: V1ValueType): boolean {
  const { baseType } = unwrapType(type);
  return baseType.scalar_type === 'VALUE_SCALAR_TYPE_BOOLEAN';
}

/**
 * Whether a type can only ever hold None.
 *
 * Such an input has no value for the user to provide — its row renders a note
 * instead of a field — so callers must not treat it as missing. It reaches the
 * runtime as `Value::None`.
 */
export function isNoneType(type?: V1ValueType): boolean {
  const { baseType } = unwrapType(type);
  return baseType.none_type !== undefined;
}

/**
 * Determines which input component type to use based on the ValueType
 */
export function getInputComponentType(type: V1ValueType): InputComponentType {
  // Check scalar types
  if (type.scalar_type) {
    switch (type.scalar_type) {
      case 'VALUE_SCALAR_TYPE_TEXT':
        return 'text';
      case 'VALUE_SCALAR_TYPE_NUMBER':
        return 'number';
      case 'VALUE_SCALAR_TYPE_BOOLEAN':
        return 'boolean';
      case 'VALUE_SCALAR_TYPE_DATE':
        return 'date';
      case 'VALUE_SCALAR_TYPE_DATETIME':
        return 'datetime';
      case 'VALUE_SCALAR_TYPE_TIME':
        return 'time';
      case 'VALUE_SCALAR_TYPE_FILE':
        return 'file';
      // TODO: `VALUE_SCALAR_TYPE_TIMEDELTA` has no editor and falls through to
      // a text field, which submits `{text}` where the wire field is
      // `duration`. Deferred: SPy's `TimeDelta` currently surfaces in the
      // manifest as `VALUE_SCALAR_TYPE_NUMBER`, so nothing reaches this case.
      default:
        return 'text';
    }
  }

  // Check enum type
  if (type.enum_type) {
    return 'enum';
  }

  // A `none_type` input can only ever hold None, so there is nothing to type
  // in — it renders as a note rather than an editable field.
  if (type.none_type) {
    return 'none';
  }

  // Structured types all use a textarea for JSON input.
  // `generic_dictionary_type` belongs here too: it carries key_type/value_type
  // rather than named fields, but it is still a structured value.
  if (
    type.list_type ||
    type.dictionary_type ||
    type.table_type ||
    type.any_type ||
    type.union_type ||
    type.opaque_type ||
    type.generic_dictionary_type
  ) {
    return 'complex';
  }

  // Default to text
  return 'text';
}

/**
 * Component types that render a single self-contained editor, and can
 * therefore be used as a `List[T]` element editor.
 *
 * Excludes `'file'` (a list of files uses the multi-select dropzone rather than
 * one picker per row), `'complex'` (a JSON textarea per row would be unusable),
 * `'list'` (only one level of nesting is supported) and `'none'` (nothing to
 * edit).
 */
const LEAF_COMPONENT_TYPES = new Set<InputComponentType>([
  'text',
  'number',
  'boolean',
  'date',
  'datetime',
  'time',
  'enum',
]);

/**
 * Whether a component type can be rendered as a `List[T]` element editor.
 */
export function isLeafComponentType(
  componentType: InputComponentType,
): boolean {
  return LEAF_COMPONENT_TYPES.has(componentType);
}

/**
 * Resolves the element type of a list, with optional wrappers stripped.
 * Returns undefined when the type is not a list.
 */
export function getListElementType(type: V1ValueType): V1ValueType | undefined {
  if (!type.list_type?.inner) return undefined;
  return unwrapType(type.list_type.inner).baseType;
}

/**
 * Extracts enum members from an enum type
 */
export function getEnumMembers(
  type: V1ValueType,
): Array<{ name?: string; description?: string }> {
  if (type.enum_type?.members) {
    return type.enum_type.members;
  }
  return [];
}

/**
 * Gets a hint about the complex type for display purposes
 */
export function getComplexTypeHint(
  type: V1ValueType,
): 'list' | 'dictionary' | 'table' | 'any' {
  if (type.list_type) return 'list';
  if (type.dictionary_type || type.generic_dictionary_type) return 'dictionary';
  if (type.table_type) return 'table';
  return 'any';
}

/**
 * Every `Commonv1Value` variant, named. This is the vocabulary every surface
 * that edits a config value shares: it picks the editor (`ManifestConfigValueEditor`),
 * decides what `encodeConfigValue` can round-trip, and types the step
 * workbench's params-rail slots. It used to leak as a bare `string`, which let
 * three independent literal sets drift apart — one of them spelling the boolean
 * kind `bool` while the value tag says `boolean`.
 */
export type TConfigValueKind =
  | 'text'
  | 'boolean'
  | 'number'
  | 'date'
  | 'datetime'
  | 'time'
  | 'file'
  | 'list'
  | 'dictionary'
  | 'table'
  | 'set'
  | 'duration'
  | 'timezone'
  | 'null';

/**
 * Determines which field is set in a Commonv1Value object.
 * Returns the type name or undefined if the value is empty.
 */
export function getValueType(
  value?: Commonv1Value,
): TConfigValueKind | undefined {
  if (!value) return undefined;

  if (value.text !== undefined) return 'text';
  if (value.bool_value !== undefined) return 'boolean';
  if (value.number !== undefined) return 'number';
  if (value.date !== undefined) return 'date';
  if (value.datetime !== undefined) return 'datetime';
  if (value.timeofday !== undefined) return 'time';
  if (value.file !== undefined) return 'file';
  if (value.list !== undefined) return 'list';
  if (value.dictionary !== undefined) return 'dictionary';
  if (value.table !== undefined) return 'table';
  if (value.set !== undefined) return 'set';
  if (value.duration !== undefined) return 'duration';
  if (value.time_zone !== undefined) return 'timezone';
  if (value.null_value !== undefined) return 'null';

  return undefined;
}

/**
 * Checks if a Commonv1Value has meaningful content.
 * Empty strings, empty objects, and undefined are considered empty.
 */
export function isValueEmpty(value?: Commonv1Value): boolean {
  if (!value) return true;

  const valueType = getValueType(value);
  if (!valueType) return true;

  // Text and duration are empty if they're empty strings
  if (valueType === 'text' || valueType === 'duration') {
    return !value.text?.trim() && !value.duration?.trim();
  }

  // File is empty if it has no remote or inline content
  if (valueType === 'file') {
    return !value.file?.remote && !value.file?.inline;
  }

  // A collection the user emptied out carries no information, so it counts as
  // absent: a required list with no items is still missing, and an optional one
  // should reach the runtime as None rather than an empty list.
  if (valueType === 'list') {
    return (value.list?.items?.length ?? 0) === 0;
  }
  if (valueType === 'set') {
    return (value.set?.items?.length ?? 0) === 0;
  }

  // All other types are non-empty if they have a value set
  return false;
}
